import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getImageUrl } from '../utils/api';

const CartContext = createContext();

// Типы действий для reducer
const CART_ACTIONS = {
  ADD_TO_CART: 'ADD_TO_CART',
  REMOVE_FROM_CART: 'REMOVE_FROM_CART',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART'
};

// Начальное состояние корзины
const initialState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
  totalPoints: 0
};

// Reducer для управления состоянием корзины
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_TO_CART: {
      const { product, quantity = 1 } = action.payload;
      const lineId = product.cartKey || product.id;
      const cartProduct = { ...product, itemType: product.itemType || 'physical', cartKey: lineId };
      const existingItem = state.items.find(item => (item.cartKey || item.id) === lineId);
      
      let newItems;
      if (existingItem) {
        newItems = state.items.map(item =>
          (item.cartKey || item.id) === lineId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newItems = [...state.items, { ...cartProduct, quantity }];
      }
      
      return calculateTotals({ ...state, items: newItems });
    }
    
    case CART_ACTIONS.REMOVE_FROM_CART: {
      const productId = action.payload;
      const newItems = state.items.filter(item => (item.cartKey || item.id) !== productId);
      return calculateTotals({ ...state, items: newItems });
    }
    
    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { productId, quantity } = action.payload;
      
      if (quantity <= 0) {
        const newItems = state.items.filter(item => (item.cartKey || item.id) !== productId);
        return calculateTotals({ ...state, items: newItems });
      }
      
      const newItems = state.items.map(item =>
        (item.cartKey || item.id) === productId
          ? { ...item, quantity }
          : item
      );
      
      return calculateTotals({ ...state, items: newItems });
    }
    
    case CART_ACTIONS.CLEAR_CART: {
      return initialState;
    }
    
    case CART_ACTIONS.LOAD_CART: {
      return calculateTotals({ ...state, items: action.payload });
    }
    
    default:
      return state;
  }
};

// Функция для расчета общих показателей корзины
const calculateTotals = (state) => {
  const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const physicalItems = state.items.filter(item => (item.itemType || 'physical') === 'physical');
  const totalPoints = physicalItems.reduce((sum, item) => sum + ((item.points || 0) * item.quantity), 0);
  const physicalSubtotal = physicalItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const hasDigital = state.items.some(item => item.itemType === 'digital');
  return {
    ...state,
    totalItems,
    totalPrice,
    totalPoints,
    physicalSubtotal,
    hasDigital
  };
};

const sanitizeCartImage = (url) => {
  if (!url || typeof url !== 'string') return null;
  // Уже сохранённый в localStorage битый https://site/data:...
  const brokenDataIdx = url.indexOf('/data:');
  if (brokenDataIdx !== -1) return url.slice(brokenDataIdx + 1);
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('http') || url.startsWith('/')) return url.startsWith('http') ? url : getImageUrl(url);
  return getImageUrl(url);
};

const resolveCartImage = (product) => {
  const candidates = [product.image, product.featured_image_url, product.featured_image];
  for (const candidate of candidates) {
    const resolved = sanitizeCartImage(candidate);
    if (resolved) {
      // #region agent log
      if (resolved.startsWith('data:') || (typeof candidate === 'string' && candidate.includes('/data:'))) {
        fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'CartContext.js:resolveCartImage',message:'resolved data/broken image',data:{id:product.id,resolvedPrefix:resolved.slice(0,40)},timestamp:Date.now(),hypothesisId:'C',runId:'post-fix'})}).catch(()=>{});
      }
      // #endregion
      return resolved;
    }
  }
  return '/nofoto.png';
};

// Провайдер контекста корзины
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user, isAuthenticated } = useAuth();
  
  // Получаем ключ корзины для текущего пользователя
  const getCartKey = () => {
    if (isAuthenticated() && user) {
      return `cart_${user.id}`;
    }
    return 'cart_guest';
  };
  
  // Загружаем корзину из localStorage при инициализации или смене пользователя
  useEffect(() => {
    const cartKey = getCartKey();
    const savedCart = localStorage.getItem(cartKey);
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        
        if (parsedCart.length > 0) {
          // Сначала загружаем корзину из localStorage (чтобы UI сразу отобразился)
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: parsedCart });
          
          // Затем ВСЕГДА обновляем цены и данные товаров из API
          // Это предотвращает ошибку "Несоответствие цены заказа" при оплате
          console.log('🔄 Обновляем данные товаров в корзине из API...');
          // #region agent log
          fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'CartContext.js:refresh-start',message:'cart refresh start',data:{count:parsedCart.length,items:parsedCart.map(i=>({id:i.id,itemType:i.itemType||'physical',hasImage:!!i.image,imagePrefix:typeof i.image==='string'?i.image.slice(0,40):null}))},timestamp:Date.now(),hypothesisId:'A',runId:'post-fix'})}).catch(()=>{});
          // #endregion
          Promise.all(
            parsedCart.map(async (item) => {
              const isDigital = item.itemType === 'digital' || String(item.id).startsWith('digital-');
              if (isDigital) {
                // #region agent log
                fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'CartContext.js:skip-digital',message:'skip digital from products API',data:{id:item.id},timestamp:Date.now(),hypothesisId:'B',runId:'post-fix'})}).catch(()=>{});
                // #endregion
                return { ...item, image: resolveCartImage(item) };
              }
              try {
                const response = await fetch(`/api/products/${item.id}`);
                // #region agent log
                fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'CartContext.js:refresh-item',message:'products API refresh result',data:{id:item.id,status:response.status,ok:response.ok},timestamp:Date.now(),hypothesisId:'A',runId:'post-fix'})}).catch(()=>{});
                // #endregion
                if (response.status === 404) {
                  // товар удалён — выкидываем из корзины
                  return null;
                }
                if (response.ok) {
                  const data = await response.json();
                  if (data.success && data.data) {
                    const freshPrice = parseFloat(data.data.price);
                    if (!isNaN(freshPrice) && freshPrice !== item.price) {
                      console.log(`💰 Цена товара "${item.name}" обновлена: ${item.price} → ${freshPrice}`);
                    }
                    const nextImage = sanitizeCartImage(data.data.featured_image_url)
                        || sanitizeCartImage(data.data.featured_image)
                        || sanitizeCartImage(item.image)
                        || resolveCartImage(item);
                    return {
                      ...item,
                      price: !isNaN(freshPrice) ? freshPrice : item.price,
                      name: data.data.name || item.name,
                      brand: data.data.brand || data.data.brand_name || item.brand,
                      points: data.data.points || item.points || 0,
                      image: nextImage,
                      featured_image: data.data.featured_image || item.featured_image
                    };
                  }
                }
              } catch (error) {
                console.error(`Ошибка обновления товара ${item.id}:`, error);
                // #region agent log
                fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'CartContext.js:refresh-catch',message:'refresh fetch failed',data:{id:item.id,err:String(error&&error.message||error)},timestamp:Date.now(),hypothesisId:'E',runId:'post-fix'})}).catch(()=>{});
                // #endregion
              }
              return { ...item, image: resolveCartImage(item) };
            })
          ).then((updatedItems) => {
            const kept = updatedItems.filter(Boolean);
            // #region agent log
            fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'CartContext.js:refresh-done',message:'cart refresh done',data:{before:parsedCart.length,after:kept.length,removed:parsedCart.length-kept.length},timestamp:Date.now(),hypothesisId:'A',runId:'post-fix'})}).catch(()=>{});
            // #endregion
            console.log('✅ Товары в корзине актуализированы');
            dispatch({ type: CART_ACTIONS.LOAD_CART, payload: kept });
          });
        } else {
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: parsedCart });
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem(cartKey);
      }
    } else {
      // Очищаем корзину при смене пользователя
      dispatch({ type: CART_ACTIONS.CLEAR_CART });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getCartKey stable
  }, [user, isAuthenticated]);

  // Сохраняем корзину в localStorage при изменении
  useEffect(() => {
    const cartKey = getCartKey();

    if (state.items.length > 0) {
      localStorage.setItem(cartKey, JSON.stringify(state.items));
    } else {
      localStorage.removeItem(cartKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getCartKey stable
  }, [state.items, user]);
  
  // Функция для добавления товара в корзину
  const addToCart = (product, quantity = 1) => {
    const image = resolveCartImage(product);
    dispatch({
      type: CART_ACTIONS.ADD_TO_CART,
      payload: {
        product: {
          ...product,
          image,
          itemType: 'physical'
        },
        quantity
      }
    });
  };

  const addDigitalToCart = (digitalProduct, quantity = 1) => {
    const dp = {
      ...digitalProduct,
      id: `digital-${digitalProduct.id}`,
      digitalId: digitalProduct.id,
      cartKey: `digital-${digitalProduct.id}`,
      itemType: 'digital',
      name: digitalProduct.title,
      featured_image: digitalProduct.cover_image,
      image: getImageUrl(digitalProduct.cover_image) || '/nofoto.png'
    };
    dispatch({ type: CART_ACTIONS.ADD_TO_CART, payload: { product: dp, quantity } });
  };

  const addCollectionToCart = (products) => {
    (products || []).forEach(p => addToCart(p, 1));
  };
  
  // Функция для удаления товара из корзины
  const removeFromCart = (productId) => {
    dispatch({
      type: CART_ACTIONS.REMOVE_FROM_CART,
      payload: productId
    });
  };
  
  // Функция для обновления количества товара
  const updateQuantity = (productId, quantity) => {
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { productId, quantity }
    });
  };
  
  // Функция для очистки корзины
  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };
  
  // Функция для получения товара из корзины
  const getItemFromCart = (productId) => {
    return state.items.find(item => item.id === productId);
  };
  
  // Функция для проверки, есть ли товар в корзине
  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId);
  };
  
  // Функция для получения количества конкретного товара в корзине
  const getItemQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };
  
  const value = {
    // Состояние
    items: state.items,
    totalItems: state.totalItems,
    totalPrice: state.totalPrice,
    totalPoints: state.totalPoints,
    physicalSubtotal: state.physicalSubtotal || state.totalPrice,
    hasDigital: state.hasDigital || false,
    
    // Функции
    addToCart,
    addDigitalToCart,
    addCollectionToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemFromCart,
    isInCart,
    getItemQuantity
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Hook для использования контекста корзины
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 